import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock,
  Database,
  ExternalLink,
  Loader2,
  Package,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Shield,
  Trash2,
  Users,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  type AdminLicense,
  type AdminModule,
  type AdminTenantDetail,
  adminCancelTenantDeletion,
  adminForceDeleteTenant,
  adminPauseTenant,
  adminResumeTenant,
  adminScheduleTenantDeletion,
  getAdminModules,
  getAdminTenant,
  getCurrentUser,
  grantTenantLicense,
  impersonateTenant,
  installTenantModule,
  PlatformApiError,
  type PlatformUser,
  revokeTenantLicense,
  uninstallTenantModule,
} from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform/tenants/$tenantId')({
  component: TenantDetailPage,
})

function TenantDetailPage() {
  const { tenantId } = Route.useParams()
  const navigate = useNavigate()

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; tenant: AdminTenantDetail; modules: AdminModule[]; me: PlatformUser }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  // Mutation state kept in a single object so the UI can disable
  // everything during an in-flight grant/revoke, and so we can
  // show a per-module "revoking…" spinner without tracking a
  // separate state per row.
  const [mutation, setMutation] = useState<{ kind: 'idle' } | { kind: 'busy'; moduleKey: string }>({
    kind: 'idle',
  })
  const [mutationError, setMutationError] = useState<string | null>(null)
  // Success-path notice for cascade side effects (e.g.
  // "uninstalling HR auto-paused 3 workflows"). Separate from
  // mutationError so a successful install with side effects
  // doesn't look like a failure.
  const [mutationNotice, setMutationNotice] = useState<string | null>(null)
  // Impersonate dialog / in-flight state — kept separate from
  // license mutation state so the license table stays interactive
  // while a user is confirming the impersonation.
  const [impersonating, setImpersonating] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [impersonateError, setImpersonateError] = useState<string | null>(null)

  // Lifecycle override. Single busy flag because the four actions
  // are mutually exclusive by current status, and stacking them in
  // one place keeps the UI state small.
  const [lifecycleBusy, setLifecycleBusy] = useState(false)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)
  const [showScheduleDeleteConfirm, setShowScheduleDeleteConfirm] = useState(false)
  const [showForceDeleteConfirm, setShowForceDeleteConfirm] = useState(false)

  const reload = useMemo(() => {
    return async () => {
      try {
        const [tenant, modules, me] = await Promise.all([
          getAdminTenant(tenantId),
          getAdminModules(),
          getCurrentUser(),
        ])
        setState({ status: 'loaded', tenant, modules, me })
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load tenant'
        setState({ status: 'error', message })
      }
    }
  }, [tenantId])

  useEffect(() => {
    const ctrl = new AbortController()
    Promise.all([
      getAdminTenant(tenantId, ctrl.signal),
      getAdminModules(ctrl.signal),
      getCurrentUser(ctrl.signal),
    ])
      .then(([tenant, modules, me]) => setState({ status: 'loaded', tenant, modules, me }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load tenant'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [tenantId])

  async function handleImpersonate() {
    setShowConfirm(false)
    setImpersonating(true)
    setImpersonateError(null)
    try {
      const { url } = await impersonateTenant(tenantId)
      window.location.href = url
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message
          ? err.message
          : 'Failed to start impersonation'
      setImpersonateError(message)
      setImpersonating(false)
    }
  }

  async function handleAdminPause() {
    if (state.status !== 'loaded') return
    if (!confirm(`Pause ${state.tenant.name}? This blocks all writes for the workspace.`)) return
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      await adminPauseTenant(tenantId)
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to pause workspace'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  async function handleAdminResume() {
    if (state.status !== 'loaded') return
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      await adminResumeTenant(tenantId)
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to resume workspace'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  async function handleAdminScheduleDelete() {
    if (state.status !== 'loaded') return
    setShowScheduleDeleteConfirm(false)
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      await adminScheduleTenantDeletion(tenantId)
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to schedule deletion'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  async function handleAdminCancelDelete() {
    if (state.status !== 'loaded') return
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      await adminCancelTenantDeletion(tenantId)
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to cancel deletion'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  async function handleForceDelete(confirmName: string) {
    if (state.status !== 'loaded') return
    setShowForceDeleteConfirm(false)
    setLifecycleBusy(true)
    setLifecycleError(null)
    try {
      await adminForceDeleteTenant(tenantId, confirmName)
      // Tenant is gone — navigate back to the list.
      void navigate({ to: '/platform/tenants' })
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message
          ? err.message
          : 'Failed to force delete workspace'
      setLifecycleError(message)
    } finally {
      setLifecycleBusy(false)
    }
  }

  async function handleGrant(moduleKey: string) {
    if (state.status !== 'loaded') return
    setMutation({ kind: 'busy', moduleKey })
    setMutationError(null)
    try {
      await grantTenantLicense({ tenantId, moduleKey, status: 'active' })
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to grant license'
      setMutationError(message)
    } finally {
      setMutation({ kind: 'idle' })
    }
  }

  async function handleRevoke(moduleKey: string) {
    if (state.status !== 'loaded') return
    setMutation({ kind: 'busy', moduleKey })
    setMutationError(null)
    try {
      await revokeTenantLicense(tenantId, moduleKey)
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to revoke license'
      setMutationError(message)
    } finally {
      setMutation({ kind: 'idle' })
    }
  }

  async function handleInstallModule(moduleKey: string) {
    if (state.status !== 'loaded') return
    setMutation({ kind: 'busy', moduleKey })
    setMutationError(null)
    setMutationNotice(null)
    try {
      const result = await installTenantModule(tenantId, moduleKey)
      // Surface the cascade re-enable count if the install
      // unblocked any previously auto-paused workflows.
      if (result.workflowsReEnabled && result.workflowsReEnabled > 0) {
        setMutationNotice(
          `Installed ${moduleKey}. Re-enabled ${result.workflowsReEnabled} workflow${
            result.workflowsReEnabled === 1 ? '' : 's'
          } that were paused while it was missing.`,
        )
      }
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to install module'
      setMutationError(message)
    } finally {
      setMutation({ kind: 'idle' })
    }
  }

  async function handleUninstallModule(moduleKey: string) {
    if (state.status !== 'loaded') return
    if (
      !confirm(
        `Uninstall "${moduleKey}" from this tenant? The module will disappear from the tenant's ERP on the next page load. Workflows that depend on its actions will be auto-paused — they reactivate when the module is reinstalled. Their license (if any) stays intact.`,
      )
    ) {
      return
    }
    setMutation({ kind: 'busy', moduleKey })
    setMutationError(null)
    setMutationNotice(null)
    try {
      const result = await uninstallTenantModule(tenantId, moduleKey)
      // Surface the cascade pause count so operators don't
      // accidentally take a tenant offline without realising.
      if (result.workflowsDisabled && result.workflowsDisabled > 0) {
        setMutationNotice(
          `Uninstalled ${moduleKey}. Auto-paused ${result.workflowsDisabled} workflow${
            result.workflowsDisabled === 1 ? '' : 's'
          } that referenced its actions.`,
        )
      }
      await reload()
    } catch (err) {
      const message =
        err instanceof PlatformApiError && err.message ? err.message : 'Failed to uninstall module'
      setMutationError(message)
    } finally {
      setMutation({ kind: 'idle' })
    }
  }

  return (
    <div>
      <Link
        to="/platform/tenants"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to tenants
      </Link>

      {state.status === 'loading' ? (
        <div className="mt-8 flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading tenant…
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="mt-8 rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          {state.message}
        </div>
      ) : null}

      {state.status === 'loaded' ? (
        <LoadedView
          tenant={state.tenant}
          modules={state.modules}
          mutation={mutation}
          mutationError={mutationError}
          mutationNotice={mutationNotice}
          onDismissNotice={() => setMutationNotice(null)}
          onGrant={handleGrant}
          onRevoke={handleRevoke}
          onInstallModule={handleInstallModule}
          onUninstallModule={handleUninstallModule}
          canImpersonate={state.me.role === 'platform_admin'}
          impersonating={impersonating}
          impersonateError={impersonateError}
          onImpersonateClick={() => setShowConfirm(true)}
          lifecycleBusy={lifecycleBusy}
          lifecycleError={lifecycleError}
          onAdminPause={handleAdminPause}
          onAdminResume={handleAdminResume}
          onAdminScheduleDeleteClick={() => setShowScheduleDeleteConfirm(true)}
          onAdminCancelDelete={handleAdminCancelDelete}
          onForceDeleteClick={() => setShowForceDeleteConfirm(true)}
        />
      ) : null}

      {showForceDeleteConfirm && state.status === 'loaded' ? (
        <ForceDeleteDialog
          tenantName={state.tenant.name}
          busy={lifecycleBusy}
          onConfirm={handleForceDelete}
          onCancel={() => setShowForceDeleteConfirm(false)}
        />
      ) : null}

      {showConfirm && state.status === 'loaded' ? (
        <ImpersonateConfirmDialog
          tenantName={state.tenant.name}
          onConfirm={handleImpersonate}
          onCancel={() => setShowConfirm(false)}
        />
      ) : null}

      {showScheduleDeleteConfirm && state.status === 'loaded' ? (
        <AdminScheduleDeleteDialog
          tenantName={state.tenant.name}
          busy={lifecycleBusy}
          onConfirm={handleAdminScheduleDelete}
          onCancel={() => setShowScheduleDeleteConfirm(false)}
        />
      ) : null}
    </div>
  )
}

function LoadedView({
  tenant,
  modules,
  mutation,
  mutationError,
  mutationNotice,
  onDismissNotice,
  onGrant,
  onRevoke,
  onInstallModule,
  onUninstallModule,
  canImpersonate,
  impersonating,
  impersonateError,
  onImpersonateClick,
  lifecycleBusy,
  lifecycleError,
  onAdminPause,
  onAdminResume,
  onAdminScheduleDeleteClick,
  onAdminCancelDelete,
  onForceDeleteClick,
}: {
  tenant: AdminTenantDetail
  modules: AdminModule[]
  mutation: { kind: 'idle' } | { kind: 'busy'; moduleKey: string }
  mutationError: string | null
  mutationNotice: string | null
  onDismissNotice: () => void
  onGrant: (moduleKey: string) => void
  onRevoke: (moduleKey: string) => void
  onInstallModule: (moduleKey: string) => void
  onUninstallModule: (moduleKey: string) => void
  canImpersonate: boolean
  impersonating: boolean
  impersonateError: string | null
  onImpersonateClick: () => void
  lifecycleBusy: boolean
  lifecycleError: string | null
  onAdminPause: () => void
  onAdminResume: () => void
  onAdminScheduleDeleteClick: () => void
  onAdminCancelDelete: () => void
  onForceDeleteClick: () => void
}) {
  const tenantIsActive = tenant.status === 'active'
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="mt-4 space-y-6"
    >
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-2">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">{tenant.name}</h2>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
              <code className="rounded bg-muted px-1.5 py-0.5">{tenant.slug}</code>
              <span>·</span>
              <span>{tenant.emailDomain ?? 'personal tier'}</span>
              <span>·</span>
              <TenantStatus status={tenant.status} />
              <span>·</span>
              <span>Created {formatDate(tenant.createdAt)}</span>
            </div>
          </div>
        </div>
        {canImpersonate ? (
          <div className="flex flex-col items-end gap-1.5">
            <button
              type="button"
              onClick={onImpersonateClick}
              disabled={impersonating || !tenantIsActive}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border border-warning/40 bg-warning/5 px-4 py-2 text-xs font-semibold',
                'text-warning hover:bg-warning/10 transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-warning/5',
              )}
              title={
                tenantIsActive
                  ? 'Open this workspace as yourself (audit-logged)'
                  : 'Cannot impersonate into a non-active workspace'
              }
            >
              {impersonating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ExternalLink className="h-3.5 w-3.5" />
              )}
              {impersonating ? 'Opening…' : 'Impersonate'}
            </button>
            {impersonateError ? (
              <div className="max-w-xs rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-[11px] text-destructive">
                {impersonateError}
              </div>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <MetaCard label="Tier" icon={Package}>
          <div className="font-semibold capitalize">{tenant.tier}</div>
        </MetaCard>
        <MetaCard label="Email domain verified" icon={Shield}>
          {tenant.emailDomainVerifiedAt ? (
            <div className="font-semibold text-success">Verified</div>
          ) : (
            <div className="font-semibold text-muted-foreground">Not verified</div>
          )}
        </MetaCard>
        <MetaCard label="Members" icon={Users}>
          <div className="font-semibold">{tenant.members.length}</div>
        </MetaCard>
      </div>

      <LifecycleCard
        tenant={tenant}
        busy={lifecycleBusy}
        error={lifecycleError}
        onPause={onAdminPause}
        onResume={onAdminResume}
        onScheduleDelete={onAdminScheduleDeleteClick}
        onCancelDelete={onAdminCancelDelete}
        onForceDelete={onForceDeleteClick}
      />

      <MembersCard members={tenant.members} />

      {mutationNotice ? (
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm text-emerald-700 dark:text-emerald-300">
          <span className="flex-1">{mutationNotice}</span>
          <button
            type="button"
            onClick={onDismissNotice}
            className="text-xs underline opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <InstalledModulesCard
        installedModules={tenant.modules}
        catalog={modules}
        mutation={mutation}
        onInstall={onInstallModule}
        onUninstall={onUninstallModule}
      />

      <LicensesCard
        licenses={tenant.licenses}
        modules={modules}
        mutation={mutation}
        mutationError={mutationError}
        onGrant={onGrant}
        onRevoke={onRevoke}
      />
    </motion.div>
  )
}

function MetaCard({
  label,
  icon: Icon,
  children,
}: {
  label: string
  icon: typeof Package
  children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-2 text-sm">{children}</div>
    </div>
  )
}

function MembersCard({ members }: { members: AdminTenantDetail['members'] }) {
  return (
    <section className="rounded-2xl border border-border bg-card">
      <header className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <h3 className="text-base font-semibold">Members</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Platform users who can manage this workspace from the dashboard.
          </p>
        </div>
      </header>
      {members.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-muted-foreground">No members yet.</div>
      ) : (
        <ul className="divide-y divide-border">
          {members.map((m) => (
            <li key={m.userId} className="flex items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{m.email}</div>
                {m.name ? (
                  <div className="truncate text-xs text-muted-foreground">{m.name}</div>
                ) : null}
              </div>
              <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {m.role}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Installed modules card.
 *
 * Shows every module in the catalog with an Install / Uninstall
 * button per row. The distinction from the licenses card below:
 *
 *   - LICENSES are billing / entitlement: "this tenant has paid
 *     for X, loadTenantModules will surface it if installed".
 *   - INSTALLATIONS are activation: "this tenant has X turned on,
 *     they'll see its entities / workflows / nav items at runtime".
 *
 * Most of the time an operator wants both — that's why the
 * Install button auto-grants a license when the module requires
 * one and none exists (backend handles this in one shot). The
 * operator only needs to touch Licenses directly when billing
 * concerns diverge from activation concerns.
 *
 * The component lists the FULL catalog (not just already-installed
 * ones) so the operator can see every available module and
 * decide what to enable. Already-installed rows show an
 * Uninstall button; not-yet-installed rows show Install.
 */
function InstalledModulesCard({
  installedModules,
  catalog,
  mutation,
  onInstall,
  onUninstall,
}: {
  installedModules: AdminTenantDetail['modules']
  catalog: AdminModule[]
  mutation: { kind: 'idle' } | { kind: 'busy'; moduleKey: string }
  onInstall: (moduleKey: string) => void
  onUninstall: (moduleKey: string) => void
}) {
  // Build a quick lookup so row rendering is O(1) per module.
  const installedByKey = new Map(installedModules.map((m) => [m.moduleKey, m]))
  // Stable sort: core modules first, then alphabetical by name.
  const sorted = [...catalog].sort((a, b) => {
    if (a.isCore !== b.isCore) return a.isCore ? -1 : 1
    return a.name.localeCompare(b.name)
  })

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Package className="h-4 w-4 text-muted-foreground" />
            Installed modules
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Every built-in module. Install adds a{' '}
            <code className="rounded bg-muted px-1">tenant_modules</code> row and auto-grants a
            license if the module requires one — the tenant sees the module the next time their ERP
            loads.
          </p>
        </div>
      </header>

      {sorted.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-border bg-muted/30 p-4 text-xs text-muted-foreground">
          The module catalog is empty. Restart the backend to sync built-in manifests from{' '}
          <code className="rounded bg-muted px-1">packages/modules/src/manifests/</code>.
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {sorted.map((module) => {
            const installed = installedByKey.get(module.key)
            const busy = mutation.kind === 'busy' && mutation.moduleKey === module.key
            return (
              <li
                key={module.key}
                className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate text-sm font-medium">{module.name}</div>
                    <code className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{module.key}</code>
                    {module.isCore ? (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary ring-1 ring-primary/25">
                        Core
                      </span>
                    ) : null}
                    {module.requiresLicense ? (
                      <span className="inline-flex items-center rounded-full bg-warning/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning ring-1 ring-warning/25">
                        License
                      </span>
                    ) : null}
                    {installed ? (
                      <span className="inline-flex items-center rounded-full bg-success/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-success ring-1 ring-success/25">
                        Installed
                      </span>
                    ) : null}
                  </div>
                  {module.description ? (
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {module.description}
                    </div>
                  ) : null}
                </div>
                {module.isCore ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground">
                    Always on
                  </span>
                ) : installed ? (
                  <button
                    type="button"
                    onClick={() => onUninstall(module.key)}
                    disabled={busy}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-medium',
                      'hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors',
                      'disabled:opacity-40 disabled:cursor-not-allowed',
                    )}
                  >
                    {busy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                    Uninstall
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onInstall(module.key)}
                    disabled={busy}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold',
                      'bg-primary text-primary-foreground shadow-sm',
                      'hover:opacity-90 transition-opacity',
                      'disabled:opacity-40 disabled:cursor-not-allowed',
                    )}
                  >
                    {busy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                    Install
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function LicensesCard({
  licenses,
  modules,
  mutation,
  mutationError,
  onGrant,
  onRevoke,
}: {
  licenses: AdminLicense[]
  modules: AdminModule[]
  mutation: { kind: 'idle' } | { kind: 'busy'; moduleKey: string }
  mutationError: string | null
  onGrant: (moduleKey: string) => void
  onRevoke: (moduleKey: string) => void
}) {
  const [picker, setPicker] = useState('')

  // Modules this tenant does NOT yet have a license for. The
  // picker shows these only — no point offering modules that
  // already have a row.
  const ungrantedModules = useMemo(() => {
    const licensedKeys = new Set(licenses.map((l) => l.moduleKey))
    return modules.filter((m) => !licensedKeys.has(m.key))
  }, [licenses, modules])

  const busy = mutation.kind === 'busy'

  return (
    <section className="rounded-2xl border border-border bg-card">
      <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold">Licenses</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Modules this tenant has been granted. loadTenantModules filters on this table at
            runtime.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={picker}
            onChange={(e) => setPicker(e.currentTarget.value)}
            disabled={busy || ungrantedModules.length === 0}
            className={cn(
              'rounded-lg border border-border bg-background px-3 py-1.5 text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:opacity-60',
            )}
          >
            <option value="">
              {ungrantedModules.length === 0 ? 'All modules licensed' : 'Choose a module…'}
            </option>
            {ungrantedModules.map((m) => (
              <option key={m.key} value={m.key}>
                {m.name} ({m.key})
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (picker) {
                onGrant(picker)
                setPicker('')
              }
            }}
            disabled={busy || !picker}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium',
              'bg-primary text-primary-foreground shadow-sm',
              'hover:opacity-90 transition-opacity',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            {busy && mutation.kind === 'busy' && mutation.moduleKey === picker ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Grant
          </button>
        </div>
      </header>

      {mutationError ? (
        <div className="mx-5 mt-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {mutationError}
        </div>
      ) : null}

      {licenses.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-muted-foreground">
          No licenses granted yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-5 py-2.5 text-left font-semibold">Module</th>
                <th className="px-5 py-2.5 text-left font-semibold">Status</th>
                <th className="px-5 py-2.5 text-left font-semibold">Granted</th>
                <th className="px-5 py-2.5 text-left font-semibold">Expires</th>
                <th className="px-5 py-2.5 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {licenses.map((license) => {
                const isRevoking = busy && mutation.moduleKey === license.moduleKey
                return (
                  <tr key={license.id} className="hover:bg-accent/40">
                    <td className="px-5 py-3 align-top">
                      <div className="font-medium">{license.moduleName ?? license.moduleKey}</div>
                      <div className="text-xs text-muted-foreground">
                        <code>{license.moduleKey}</code>
                      </div>
                    </td>
                    <td className="px-5 py-3 align-top">
                      <LicenseStatus status={license.status} />
                    </td>
                    <td className="px-5 py-3 align-top text-xs text-muted-foreground">
                      {formatDate(license.grantedAt)}
                    </td>
                    <td className="px-5 py-3 align-top text-xs text-muted-foreground">
                      {license.expiresAt ? formatDate(license.expiresAt) : 'Never'}
                    </td>
                    <td className="px-5 py-3 text-right align-top">
                      <button
                        type="button"
                        onClick={() => onRevoke(license.moduleKey)}
                        disabled={busy}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-medium',
                          'hover:border-destructive hover:text-destructive transition-colors',
                          'disabled:opacity-60 disabled:cursor-not-allowed',
                        )}
                      >
                        {isRevoking ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                        Revoke
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function TenantStatus({ status }: { status: string | null }) {
  const tone =
    status === 'active'
      ? 'text-success'
      : status === 'paused' || status === 'suspended'
        ? 'text-warning'
        : status === 'deleted'
          ? 'text-destructive'
          : 'text-muted-foreground'
  return <span className={cn('font-semibold', tone)}>{status ?? '—'}</span>
}

function LicenseStatus({ status }: { status: string }) {
  const tone =
    status === 'active'
      ? 'bg-success/10 text-success ring-success/25'
      : status === 'trial'
        ? 'bg-info/10 text-info ring-info/25'
        : status === 'expired' || status === 'revoked'
          ? 'bg-destructive/10 text-destructive ring-destructive/25'
          : 'bg-muted text-muted-foreground ring-border'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        tone,
      )}
    >
      {status === 'active' ? <Check className="h-2.5 w-2.5" /> : null}
      {status}
    </span>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

/**
 * Confirmation dialog for admin impersonation. The action is
 * irreversible in the sense that it writes an audit log entry and
 * creates a real tenant user record — we want the admin to stop
 * and read what they're about to do before committing.
 *
 * Styled as a plain centered modal — no Radix dep. Clicking the
 * overlay cancels. Keyboard escape would be nice but isn't wired
 * up; add if it turns out to matter.
 */
function ImpersonateConfirmDialog({
  tenantName,
  onConfirm,
  onCancel,
}: {
  tenantName: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop — a real <button> so click-to-dismiss is a first-
          class interactive element and the a11y rule is happy. */}
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onCancel}
        className="absolute inset-0 h-full w-full cursor-default bg-background/80 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl shadow-primary/10"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-2">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-warning/10 text-warning ring-1 ring-warning/25">
            <Shield className="h-4 w-4" />
          </div>
          <h3 className="text-base font-semibold tracking-tight">Impersonate workspace?</h3>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          You're about to open <span className="font-semibold text-foreground">{tenantName}</span>{' '}
          as yourself. A shadow user record will be created in this workspace's database on first
          hop, and this action is written to the platform audit log.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Use this only for support or debugging — normal admin workflows should go through the
          tenant owner.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={cn(
              'inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium',
              'hover:bg-accent hover:text-accent-foreground transition-colors',
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold',
              // text-white — the theme has no --warning-foreground
              // token, so text-warning-foreground resolves to nothing
              // and the button inherits whatever color the parent
              // cascaded down.
              'bg-warning !text-white shadow-md shadow-warning/20',
              'hover:opacity-90 transition-opacity',
            )}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Impersonate
          </button>
        </div>
      </motion.div>
    </div>
  )
}

/**
 * Grace window, in days, for admin-scheduled deletions. Must match
 * the backend's `DELETE_GRACE_DAYS` constant in platform.ts — keep
 * them in sync. Copied here so the dialog can render the exact
 * number without a server round trip.
 */
const ADMIN_DELETE_GRACE_DAYS = 30

/**
 * Admin lifecycle card. Rendered on the admin tenant detail page
 * between the MetaCards and the MembersCard. The buttons it shows
 * depend on the workspace's current status:
 *
 *   - active           → Pause, Schedule deletion
 *   - paused           → Resume, Schedule deletion
 *   - pending_deletion → Cancel deletion (with countdown)
 *
 * These are admin overrides — the same actions the owner gets on
 * /account/workspaces/:id, but gated on ADMIN_WRITE_ROLES instead
 * of tenant membership. Every action writes an audit row with
 * `metadata.viaAdminOverride=true` so the audit viewer can show
 * admin-initiated lifecycle changes distinctly from owner ones.
 */
function LifecycleCard({
  tenant,
  busy,
  error,
  onPause,
  onResume,
  onScheduleDelete,
  onCancelDelete,
  onForceDelete,
}: {
  tenant: AdminTenantDetail
  busy: boolean
  error: string | null
  onPause: () => void
  onResume: () => void
  onScheduleDelete: () => void
  onCancelDelete: () => void
  onForceDelete: () => void
}) {
  const status = tenant.status
  const isPaused = status === 'paused'
  const isPending = status === 'pending_deletion'
  const isActive = status === 'active'
  // Anything outside the known three is a status we can't act on
  // from here — 'deleted', 'suspended', legacy nulls, etc. Show
  // the card with a disabled message so the admin isn't confused.
  const isUnknown = !isActive && !isPaused && !isPending

  const countdown =
    isPending && tenant.scheduledDeleteAt ? formatCountdownDays(tenant.scheduledDeleteAt) : null

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Workspace lifecycle
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Admin overrides — pause, resume, schedule deletion, or cancel a pending deletion without
            needing to be a member of the workspace. Every action is audit-logged as a platform
            admin action.
          </p>
        </div>
        <LifecycleStatusPill status={status} countdown={countdown} />
      </div>

      {error ? (
        <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      ) : null}

      {isUnknown ? (
        <div className="mt-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          This workspace is in status{' '}
          <code className="rounded bg-muted px-1">{status ?? 'null'}</code>. Lifecycle overrides
          only apply to active, paused, or pending_deletion workspaces.
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {isActive ? (
          <>
            <LifecycleButton
              variant="warning"
              icon={Pause}
              label="Pause"
              busy={busy}
              onClick={onPause}
            />
            <LifecycleButton
              variant="destructive"
              icon={Trash2}
              label="Schedule deletion…"
              busy={busy}
              onClick={onScheduleDelete}
            />
          </>
        ) : null}
        {isPaused ? (
          <>
            <LifecycleButton
              variant="success"
              icon={Play}
              label="Resume"
              busy={busy}
              onClick={onResume}
            />
            <LifecycleButton
              variant="destructive"
              icon={Trash2}
              label="Schedule deletion…"
              busy={busy}
              onClick={onScheduleDelete}
            />
          </>
        ) : null}
        {isPending ? (
          <LifecycleButton
            variant="success"
            icon={RotateCcw}
            label="Cancel deletion"
            busy={busy}
            onClick={onCancelDelete}
          />
        ) : null}
      </div>

      {/* Force delete — always visible, separated from the soft lifecycle
          actions above. This is the nuclear option. */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-destructive">Danger zone</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Immediately destroys the database and removes the tenant. This cannot be undone.
            </p>
          </div>
          <LifecycleButton
            variant="destructive"
            icon={Trash2}
            label="Force delete"
            busy={busy}
            onClick={onForceDelete}
          />
        </div>
      </div>
    </div>
  )
}

function LifecycleStatusPill({
  status,
  countdown,
}: {
  status: AdminTenantDetail['status']
  countdown: string | null
}) {
  if (status === 'paused') {
    return (
      <span className="inline-flex flex-none items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning ring-1 ring-warning/25">
        <Pause className="h-3 w-3" />
        Paused
      </span>
    )
  }
  if (status === 'pending_deletion') {
    return (
      <span className="inline-flex flex-none items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive ring-1 ring-destructive/25">
        <AlertTriangle className="h-3 w-3" />
        {countdown ?? 'Deleting'}
      </span>
    )
  }
  if (status === 'active') {
    return (
      <span className="inline-flex flex-none items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success ring-1 ring-success/25">
        <Check className="h-3 w-3" />
        Active
      </span>
    )
  }
  return (
    <span className="inline-flex flex-none items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
      {status ?? 'Unknown'}
    </span>
  )
}

function LifecycleButton({
  variant,
  icon: Icon,
  label,
  busy,
  onClick,
}: {
  variant: 'success' | 'warning' | 'destructive'
  icon: typeof Pause
  label: string
  busy: boolean
  onClick: () => void
}) {
  const tone =
    variant === 'success'
      ? 'border-success/40 bg-success/5 text-success hover:bg-success/10'
      : variant === 'warning'
        ? 'border-warning/40 bg-warning/5 text-warning hover:bg-warning/10'
        : 'border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive/10'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold',
        'transition-colors',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        tone,
      )}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  )
}

/**
 * Confirm dialog for admin-initiated schedule deletion. Simpler
 * than the owner-facing one (no type-to-confirm) because admins
 * are power users and the destination is still a 30-day grace
 * window, not immediate destruction. Copy explicitly explains
 * that the deletion is reversible during the grace period.
 */
function AdminScheduleDeleteDialog({
  tenantName,
  busy,
  onConfirm,
  onCancel,
}: {
  tenantName: string
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onCancel}
        disabled={busy}
        className="absolute inset-0 h-full w-full cursor-default bg-background/80 backdrop-blur-sm disabled:cursor-not-allowed"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-destructive/40 bg-card p-6 shadow-xl shadow-destructive/10"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-2">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10 text-destructive ring-1 ring-destructive/25">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <h3 className="text-base font-semibold tracking-tight">Schedule workspace deletion</h3>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{tenantName}</span> will enter a{' '}
          <span className="font-semibold text-foreground">{ADMIN_DELETE_GRACE_DAYS}-day</span>{' '}
          read-only grace period, then be permanently dropped by the cleanup cron. You (or the
          owner) can cancel the deletion any time during the grace window.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          This action will be recorded in the platform audit log with your admin email.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={cn(
              'inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium',
              'hover:bg-accent hover:text-accent-foreground transition-colors',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold',
              'bg-destructive text-destructive-foreground shadow-md shadow-destructive/20',
              'hover:opacity-90 transition-opacity',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            {busy ? 'Scheduling…' : `Schedule deletion in ${ADMIN_DELETE_GRACE_DAYS} days`}
          </button>
        </div>
      </motion.div>
    </div>
  )
}

function ForceDeleteDialog({
  tenantName,
  busy,
  onConfirm,
  onCancel,
}: {
  tenantName: string
  busy: boolean
  onConfirm: (confirmName: string) => void
  onCancel: () => void
}) {
  const [confirmText, setConfirmText] = useState('')
  const matches = confirmText === tenantName

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onCancel}
        disabled={busy}
        className="absolute inset-0 h-full w-full cursor-default bg-background/80 backdrop-blur-sm disabled:cursor-not-allowed"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-destructive/40 bg-card p-6 shadow-xl shadow-destructive/10"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-2">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10 text-destructive ring-1 ring-destructive/25">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <h3 className="text-base font-semibold tracking-tight">Force delete workspace</h3>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          This will{' '}
          <span className="font-semibold text-destructive">immediately and permanently</span>{' '}
          destroy the workspace <span className="font-semibold text-foreground">{tenantName}</span>.
          The Postgres database will be dropped, all records lost, and the control DB rows
          cascade-deleted. There is no grace period and no undo.
        </p>
        <div className="mt-4">
          <label htmlFor="force-delete-confirm" className="text-xs font-medium">
            Type <span className="font-mono text-destructive">{tenantName}</span> to confirm
          </label>
          <input
            id="force-delete-confirm"
            type="text"
            // biome-ignore lint/a11y/noAutofocus: confirm field inside a delete-confirmation dialog; focus should enter the dialog
            autoFocus
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={cn(
              'mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm',
              matches ? 'border-destructive' : 'border-border',
            )}
            placeholder={tenantName}
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={cn(
              'inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium',
              'hover:bg-accent hover:text-accent-foreground transition-colors',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(confirmText)}
            disabled={busy || !matches}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold',
              'bg-destructive text-destructive-foreground shadow-md shadow-destructive/20',
              'hover:opacity-90 transition-opacity',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            )}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Force delete permanently
          </button>
        </div>
      </motion.div>
    </div>
  )
}

function formatCountdownDays(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms) || ms <= 0) return 'Expired'
  const days = Math.floor(ms / (24 * 60 * 60 * 1000))
  if (days >= 2) return `${days}d left`
  if (days === 1) return '1d left'
  const hours = Math.max(1, Math.floor(ms / (60 * 60 * 1000)))
  return `${hours}h left`
}
